'use client';

import { styled } from "@linaria/react";
import Image from "next/image";
import { startTransition, useCallback, useEffect, useState } from "react";
import { faArrowLeftLong } from "@fortawesome/free-solid-svg-icons/faArrowLeftLong";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffectEvent } from "@floating-ui/react/utils";
import "./HTTPErrorPage.linaria.module.css!=!./HTTPErrorPage?./HTTPErrorPage.linaria.module.css?css=H4sIAAAAAAAAA4WS786CIBjFv3sVbu/aaguH/d3gahBQMeExwKyc9/5OzWa11jf5nePDOUCU+6PY3C+tUK4q2Y2gRiYn5VECV/rO0lLOoHYDmBsHw9yNhLKSewWGcChrbej026fyxf+YxkqVGaS81I5waby0dBZ1lJ/CtMcL/TGhYvz0MeAFPsxF7bxKb4iD8dL4Sf6CKyaEMhnRyiz3l2YdHqzUK5qC8cipuyRxtNlbqWkXRPktPdsibROwQlpkmVC1I3Gv9gldzgQ0BIc47Fn4l/LdjmpmM2VQAt6DHs1dEGVxIc9Gl23C+CmzUBuBOJRgSe7KZXzcr8MY48U63ODFio5Kkysv6bg5MWCm7ykIjoagUyMcHft1GI941qhf8to6sKQC9XJ43jLj1HDB78lCHG0d/Wnont1IDhdpfzbc9g27IOLnawGlaBslfE4OuE/p5dW/vpIu+AcJo7jSEwMAAA=="
const HTTPErrorPageWrapper = /*#__PURE__*/styled('main')({
  name: "HTTPErrorPageWrapper",
  class: "ht7d2zv",
  propsAsIs: false
});
const _exp = /*#__PURE__*/() => Image;
const HTTPCatImage = /*#__PURE__*/styled(_exp())({
  name: "HTTPCatImage",
  class: "hyfqrjf",
  propsAsIs: true
});
const GoBackButton = /*#__PURE__*/styled('button')({
  name: "GoBackButton",
  class: "g1jeqnml",
  propsAsIs: false
});
const CheekyTextSection = /*#__PURE__*/styled('section')({
  name: "CheekyTextSection",
  class: "cqxjold",
  propsAsIs: false
});
export default function HTTPErrorPage({
  httpStatus,
  statusCatAltText,
  cheekyText
}: {
  readonly httpStatus?: number;
  readonly statusCatAltText: string;
  readonly cheekyText: React.ReactNode;
}) {
  // Cue the over-engineered back button that accounts for SPA navigation

  // go-back-prev-id is the history.length just before the user first arrived on this page,
  // even if they navigated (to the same page) within the SPA afterwards. We use this to
  // determine how far back to go in history to get the user back to where they came from.
  //
  // The main context to keep in mind here is that this page, at the moment, can only be
  // arrived at because the user entered broken query parameters. The Link component we have
  // currently keeps all query parameters when navigating within the SPA, so if the user
  // navigates within the SPA with broken query parameters, they will remain with broken query
  // parameters (and therefore will see this page on every URL they navigate to within the SPA).
  //
  // If the user navigated within the SPA with broken query params, we want to go back to
  // before they entered the SPA with broken query params. If the user arrived directly on
  // this page, we want to go back to the previous page in history.

  const [previousHistoryID, setPreviousHistoryIDRaw] = useState<number | null>(null); // eslint-disable-line react/hook-use-state

  const setPreviousHistoryID = useEffectEvent((id: number | null) => {
    if (id === null) sessionStorage.removeItem('go-back-prev-id');else sessionStorage.setItem('go-back-prev-id', id.toString());
    startTransition(() => setPreviousHistoryIDRaw(id));
  });
  useEffect(() => {
    const storedID = sessionStorage.getItem('go-back-prev-id');
    let targetHistoryID: number = history.length - 1;
    if (storedID !== null) {
      const storedHistoryID = parseInt(storedID, 10);
      if (!isNaN(targetHistoryID) && history.length > targetHistoryID) targetHistoryID = storedHistoryID;
    }
    setPreviousHistoryID(targetHistoryID);
  }, [setPreviousHistoryID]);
  useEffect(() => {
    const listener = () => {
      if (history.length >= (previousHistoryID ?? 0)) return; // Navigated within SPA; do nothing

      sessionStorage.removeItem('go-back-prev-id');
      setPreviousHistoryID(null);
    };
    window.addEventListener('popstate', listener);
    return () => window.removeEventListener('popstate', listener);
  }, [previousHistoryID, setPreviousHistoryID]);
  const goBack = useCallback(() => {
    setPreviousHistoryID(null);
    window.history.go(Math.min(-1, (previousHistoryID ?? 0) - (history.length ?? 0)));
  }, [previousHistoryID, setPreviousHistoryID]);
  return <HTTPErrorPageWrapper>
		<a href={`https://http.cat/status/${httpStatus}`} rel="noreferrer noopener" target="_blank">
			<HTTPCatImage src={`https://http.cat/images/${httpStatus}.jpg`} alt={statusCatAltText} width={750} height={600} loading="eager" priority />
		</a>

		<CheekyTextSection>
			{cheekyText}
		</CheekyTextSection>

		<GoBackButton type='button' onClick={goBack}>
			<FontAwesomeIcon icon={faArrowLeftLong} style={{
        marginRight: '0.5rem'
      }} />
			Go Back
		</GoBackButton>
	</HTTPErrorPageWrapper>;
}